import React        from "react"
import {Box, Button, Fade, useTheme} from "@mui/material"
import {
	AutoAwesomeOutlined,
	ChevronRight,
	HelpOutlineOutlined,
	LightbulbOutlined,
	PsychologyOutlined,
	PublicOutlined
}                    from "@mui/icons-material"


/*
 * The Quick-Start action buttons, shared by the desktop Ask Birdsy panel and the mobile
 * QuestionInput bar: they sit below the last message and above the ask input, drop out while
 * Birdsy is answering, and fade back in once the reply is complete.
 */

// mockup's per-action accents, same values in light and dark (the mockup does not override them)
const ACCENTS = {
	EXAMPLE:   ['#2563EB', '#3B82F6'],
	HINT:      ['#9333EA', '#A855F7'],
	MEMORIZE:  ['#D97706', '#F59E0B'],
	WHY_WRONG: ['#0891B2', '#06B6D4'],
}

// leading icon per action type — keyed the same way as ACCENTS above
const ICONS = {
	EXAMPLE:   PublicOutlined,
	HINT:      LightbulbOutlined,
	MEMORIZE:  PsychologyOutlined,
	WHY_WRONG: HelpOutlineOutlined,
}

const QuickStartActions = ({actions, onAction, isStreaming, hint}) => {

	const theme = useTheme()
	const custom = theme.custom
	// the accents are fixed hex values, so a 10% wash that reads well on white nearly vanishes
	// on the dark surface — give dark mode a stronger tint
	const restAlpha = theme.palette.mode === 'dark' ? '4D' : '26'
	// the accents are tuned for white; the darker `from` end of each pair sinks into the dark
	// button, so dark mode uses the lighter `to` end for the icon
	const isDark = theme.palette.mode === 'dark'

	return (
		<Fade in={!isStreaming} timeout={{enter: 400, exit: 0}} unmountOnExit={true}>
			<Box display="flex" flexDirection="column" gap={2} sx={{flexShrink: 0}}>
				{hint}
				<Box display="flex" flexDirection="column" gap={1.25} sx={{flexShrink: 0}}>
					{
						actions && actions.map(a => {
							const [from, to] = ACCENTS[a.type] || [custom.indigo.hover, custom.indigo.primary]
							const Icon = ICONS[a.type] || AutoAwesomeOutlined
							return (
								<Button
									key={`qs-${a.type}`}
									onClick={() => onAction(a)}
									fullWidth={true}
									startIcon={<Icon/>}
									endIcon={<ChevronRight/>}
									sx={{
										justifyContent: 'space-between',
										gap: 1,
										textTransform: 'none',
										textAlign: 'left',
										whiteSpace: 'normal',
										p: 1.75,
										fontSize: custom.fontSize.base,
										fontWeight: custom.fontWeight.medium,
										borderRadius: `${custom.radius.md}px`,
										// theme-aware: '#000' was invisible against the dark surface
										color: custom.text.main,
										borderLeftColor: from,
										borderLeftWidth: `3px`,
										borderLeftStyle: 'solid',
										boxShadow: custom.shadow.card,
										// resting: the hover gradient at low alpha; hover: full strength
										background: `linear-gradient(to bottom right, ${to}${restAlpha}, ${from}${restAlpha})`,
										transition: 'background 150ms, color 150ms',
										'&:hover': {color: '#fff', background: `linear-gradient(to bottom right, ${to}, ${from})`},
									'& .MuiButton-startIcon': {color: isDark ? to : from, transition: 'color 150ms'},
										'&:hover .MuiButton-startIcon': {color: '#fff'},
										'& .MuiButton-endIcon': {transition: 'transform 150ms'},
										'&:hover .MuiButton-endIcon': {transform: 'translateX(4px)'},
									}}
								>
									<Box component="span" sx={{flex: 1}}>{a.label}</Box>
								</Button>
							)
						})
					}
				</Box>
			</Box>
		</Fade>
	)
}

export default QuickStartActions
